import type { LucideIcon } from 'lucide-react'
import {
  Rocket,
  PenTool,
  FileText,
  MessageSquare,
  FolderOpen,
  BarChart3,
  HelpCircle,
} from 'lucide-react'

// ============================================================
// Types
// ============================================================

export interface HelpStep {
  /** i18n key for step title (short, e.g. "Open your document") */
  titleKey: string
  /** i18n key for step description (longer explanation) */
  descriptionKey: string
}

export interface HelpTip {
  /** i18n key for tip text */
  textKey: string
  /** 'tip' = blue, 'warning' = amber, 'important' = red */
  variant: 'tip' | 'warning' | 'important'
}

export interface HelpSection {
  /** Unique section id, used for navigation */
  id: string
  /** i18n key for section title */
  titleKey: string
  /** i18n key for section intro paragraph */
  introKey: string
  /** Numbered steps */
  steps: HelpStep[]
  /** Optional tips shown after steps */
  tips?: HelpTip[]
}

export interface HelpCategory {
  /** Unique category id */
  id: string
  /** i18n key for category title */
  titleKey: string
  /** i18n key for short description shown on overview */
  descriptionKey: string
  /** Lucide icon component */
  icon: LucideIcon
  /** Sections within this category */
  sections: HelpSection[]
}

// ============================================================
// Content definitions
// ============================================================

export const helpCategories: HelpCategory[] = [
  // ----------------------------------------------------------
  // 1. Getting Started
  // ----------------------------------------------------------
  {
    id: 'getting-started',
    titleKey: 'help.gettingStarted.title',
    descriptionKey: 'help.gettingStarted.description',
    icon: Rocket,
    sections: [
      {
        id: 'login',
        titleKey: 'help.gettingStarted.login.title',
        introKey: 'help.gettingStarted.login.intro',
        steps: [
          { titleKey: 'help.gettingStarted.login.step1.title', descriptionKey: 'help.gettingStarted.login.step1.desc' },
          { titleKey: 'help.gettingStarted.login.step2.title', descriptionKey: 'help.gettingStarted.login.step2.desc' },
          { titleKey: 'help.gettingStarted.login.step3.title', descriptionKey: 'help.gettingStarted.login.step3.desc' },
        ],
        tips: [
          { textKey: 'help.gettingStarted.login.tip1', variant: 'tip' },
        ],
      },
      {
        id: 'switch-accounts',
        titleKey: 'help.gettingStarted.accounts.title',
        introKey: 'help.gettingStarted.accounts.intro',
        steps: [
          { titleKey: 'help.gettingStarted.accounts.step1.title', descriptionKey: 'help.gettingStarted.accounts.step1.desc' },
          { titleKey: 'help.gettingStarted.accounts.step2.title', descriptionKey: 'help.gettingStarted.accounts.step2.desc' },
          { titleKey: 'help.gettingStarted.accounts.step3.title', descriptionKey: 'help.gettingStarted.accounts.step3.desc' },
        ],
      },
      {
        id: 'navigate',
        titleKey: 'help.gettingStarted.navigate.title',
        introKey: 'help.gettingStarted.navigate.intro',
        steps: [
          { titleKey: 'help.gettingStarted.navigate.step1.title', descriptionKey: 'help.gettingStarted.navigate.step1.desc' },
          { titleKey: 'help.gettingStarted.navigate.step2.title', descriptionKey: 'help.gettingStarted.navigate.step2.desc' },
          { titleKey: 'help.gettingStarted.navigate.step3.title', descriptionKey: 'help.gettingStarted.navigate.step3.desc' },
          { titleKey: 'help.gettingStarted.navigate.step4.title', descriptionKey: 'help.gettingStarted.navigate.step4.desc' },
        ],
      },
    ],
  },

  // ----------------------------------------------------------
  // 2. Signing Documents
  // ----------------------------------------------------------
  {
    id: 'signing',
    titleKey: 'help.signing.title',
    descriptionKey: 'help.signing.description',
    icon: PenTool,
    sections: [
      {
        id: 'send-for-signing',
        titleKey: 'help.signing.send.title',
        introKey: 'help.signing.send.intro',
        steps: [
          { titleKey: 'help.signing.send.step1.title', descriptionKey: 'help.signing.send.step1.desc' },
          { titleKey: 'help.signing.send.step2.title', descriptionKey: 'help.signing.send.step2.desc' },
          { titleKey: 'help.signing.send.step3.title', descriptionKey: 'help.signing.send.step3.desc' },
          { titleKey: 'help.signing.send.step4.title', descriptionKey: 'help.signing.send.step4.desc' },
          { titleKey: 'help.signing.send.step5.title', descriptionKey: 'help.signing.send.step5.desc' },
        ],
        tips: [
          { textKey: 'help.signing.send.tip1', variant: 'tip' },
        ],
      },
      {
        id: 'signature-fields',
        titleKey: 'help.signing.fields.title',
        introKey: 'help.signing.fields.intro',
        steps: [
          { titleKey: 'help.signing.fields.step1.title', descriptionKey: 'help.signing.fields.step1.desc' },
          { titleKey: 'help.signing.fields.step2.title', descriptionKey: 'help.signing.fields.step2.desc' },
          { titleKey: 'help.signing.fields.step3.title', descriptionKey: 'help.signing.fields.step3.desc' },
        ],
        tips: [
          { textKey: 'help.signing.fields.tip1', variant: 'important' },
        ],
      },
      {
        id: 'signature-types',
        titleKey: 'help.signing.types.title',
        introKey: 'help.signing.types.intro',
        steps: [
          { titleKey: 'help.signing.types.step1.title', descriptionKey: 'help.signing.types.step1.desc' },
          { titleKey: 'help.signing.types.step2.title', descriptionKey: 'help.signing.types.step2.desc' },
        ],
      },
      {
        id: 'reminders',
        titleKey: 'help.signing.reminders.title',
        introKey: 'help.signing.reminders.intro',
        steps: [
          { titleKey: 'help.signing.reminders.step1.title', descriptionKey: 'help.signing.reminders.step1.desc' },
          { titleKey: 'help.signing.reminders.step2.title', descriptionKey: 'help.signing.reminders.step2.desc' },
          { titleKey: 'help.signing.reminders.step3.title', descriptionKey: 'help.signing.reminders.step3.desc' },
        ],
      },
    ],
  },

  // ----------------------------------------------------------
  // 3. Templates & Variables
  // ----------------------------------------------------------
  {
    id: 'templates',
    titleKey: 'help.templates.title',
    descriptionKey: 'help.templates.description',
    icon: FileText,
    sections: [
      {
        id: 'create-template',
        titleKey: 'help.templates.create.title',
        introKey: 'help.templates.create.intro',
        steps: [
          { titleKey: 'help.templates.create.step1.title', descriptionKey: 'help.templates.create.step1.desc' },
          { titleKey: 'help.templates.create.step2.title', descriptionKey: 'help.templates.create.step2.desc' },
          { titleKey: 'help.templates.create.step3.title', descriptionKey: 'help.templates.create.step3.desc' },
          { titleKey: 'help.templates.create.step4.title', descriptionKey: 'help.templates.create.step4.desc' },
          { titleKey: 'help.templates.create.step5.title', descriptionKey: 'help.templates.create.step5.desc' },
        ],
      },
      {
        id: 'use-variables',
        titleKey: 'help.templates.variables.title',
        introKey: 'help.templates.variables.intro',
        steps: [
          { titleKey: 'help.templates.variables.step1.title', descriptionKey: 'help.templates.variables.step1.desc' },
          { titleKey: 'help.templates.variables.step2.title', descriptionKey: 'help.templates.variables.step2.desc' },
          { titleKey: 'help.templates.variables.step3.title', descriptionKey: 'help.templates.variables.step3.desc' },
        ],
        tips: [
          { textKey: 'help.templates.variables.tip1', variant: 'tip' },
        ],
      },
      {
        id: 'generate-from-template',
        titleKey: 'help.templates.generate.title',
        introKey: 'help.templates.generate.intro',
        steps: [
          { titleKey: 'help.templates.generate.step1.title', descriptionKey: 'help.templates.generate.step1.desc' },
          { titleKey: 'help.templates.generate.step2.title', descriptionKey: 'help.templates.generate.step2.desc' },
          { titleKey: 'help.templates.generate.step3.title', descriptionKey: 'help.templates.generate.step3.desc' },
          { titleKey: 'help.templates.generate.step4.title', descriptionKey: 'help.templates.generate.step4.desc' },
        ],
      },
    ],
  },

  // ----------------------------------------------------------
  // 4. Negotiations
  // ----------------------------------------------------------
  {
    id: 'negotiations',
    titleKey: 'help.negotiations.title',
    descriptionKey: 'help.negotiations.description',
    icon: MessageSquare,
    sections: [
      {
        id: 'start-negotiation',
        titleKey: 'help.negotiations.start.title',
        introKey: 'help.negotiations.start.intro',
        steps: [
          { titleKey: 'help.negotiations.start.step1.title', descriptionKey: 'help.negotiations.start.step1.desc' },
          { titleKey: 'help.negotiations.start.step2.title', descriptionKey: 'help.negotiations.start.step2.desc' },
          { titleKey: 'help.negotiations.start.step3.title', descriptionKey: 'help.negotiations.start.step3.desc' },
        ],
      },
      {
        id: 'comments',
        titleKey: 'help.negotiations.comments.title',
        introKey: 'help.negotiations.comments.intro',
        steps: [
          { titleKey: 'help.negotiations.comments.step1.title', descriptionKey: 'help.negotiations.comments.step1.desc' },
          { titleKey: 'help.negotiations.comments.step2.title', descriptionKey: 'help.negotiations.comments.step2.desc' },
          { titleKey: 'help.negotiations.comments.step3.title', descriptionKey: 'help.negotiations.comments.step3.desc' },
          { titleKey: 'help.negotiations.comments.step4.title', descriptionKey: 'help.negotiations.comments.step4.desc' },
        ],
        tips: [
          { textKey: 'help.negotiations.comments.tip1', variant: 'tip' },
        ],
      },
      {
        id: 'manage-comments',
        titleKey: 'help.negotiations.manage.title',
        introKey: 'help.negotiations.manage.intro',
        steps: [
          { titleKey: 'help.negotiations.manage.step1.title', descriptionKey: 'help.negotiations.manage.step1.desc' },
          { titleKey: 'help.negotiations.manage.step2.title', descriptionKey: 'help.negotiations.manage.step2.desc' },
          { titleKey: 'help.negotiations.manage.step3.title', descriptionKey: 'help.negotiations.manage.step3.desc' },
        ],
        tips: [
          { textKey: 'help.negotiations.manage.tip1', variant: 'important' },
          { textKey: 'help.negotiations.manage.tip2', variant: 'tip' },
        ],
      },
      {
        id: 'comment-layout',
        titleKey: 'help.negotiations.layout.title',
        introKey: 'help.negotiations.layout.intro',
        steps: [
          { titleKey: 'help.negotiations.layout.step1.title', descriptionKey: 'help.negotiations.layout.step1.desc' },
          { titleKey: 'help.negotiations.layout.step2.title', descriptionKey: 'help.negotiations.layout.step2.desc' },
          { titleKey: 'help.negotiations.layout.step3.title', descriptionKey: 'help.negotiations.layout.step3.desc' },
          { titleKey: 'help.negotiations.layout.step4.title', descriptionKey: 'help.negotiations.layout.step4.desc' },
        ],
        tips: [
          { textKey: 'help.negotiations.layout.tip1', variant: 'warning' },
        ],
      },
      {
        id: 'versions',
        titleKey: 'help.negotiations.versions.title',
        introKey: 'help.negotiations.versions.intro',
        steps: [
          { titleKey: 'help.negotiations.versions.step1.title', descriptionKey: 'help.negotiations.versions.step1.desc' },
          { titleKey: 'help.negotiations.versions.step2.title', descriptionKey: 'help.negotiations.versions.step2.desc' },
          { titleKey: 'help.negotiations.versions.step3.title', descriptionKey: 'help.negotiations.versions.step3.desc' },
        ],
      },
      {
        id: 'finalize',
        titleKey: 'help.negotiations.finalize.title',
        introKey: 'help.negotiations.finalize.intro',
        steps: [
          { titleKey: 'help.negotiations.finalize.step1.title', descriptionKey: 'help.negotiations.finalize.step1.desc' },
          { titleKey: 'help.negotiations.finalize.step2.title', descriptionKey: 'help.negotiations.finalize.step2.desc' },
        ],
      },
    ],
  },

  // ----------------------------------------------------------
  // 5. Archive & Organize
  // ----------------------------------------------------------
  {
    id: 'archive',
    titleKey: 'help.archive.title',
    descriptionKey: 'help.archive.description',
    icon: FolderOpen,
    sections: [
      {
        id: 'archive-document',
        titleKey: 'help.archive.save.title',
        introKey: 'help.archive.save.intro',
        steps: [
          { titleKey: 'help.archive.save.step1.title', descriptionKey: 'help.archive.save.step1.desc' },
          { titleKey: 'help.archive.save.step2.title', descriptionKey: 'help.archive.save.step2.desc' },
          { titleKey: 'help.archive.save.step3.title', descriptionKey: 'help.archive.save.step3.desc' },
        ],
      },
      {
        id: 'folders-tags',
        titleKey: 'help.archive.organize.title',
        introKey: 'help.archive.organize.intro',
        steps: [
          { titleKey: 'help.archive.organize.step1.title', descriptionKey: 'help.archive.organize.step1.desc' },
          { titleKey: 'help.archive.organize.step2.title', descriptionKey: 'help.archive.organize.step2.desc' },
          { titleKey: 'help.archive.organize.step3.title', descriptionKey: 'help.archive.organize.step3.desc' },
        ],
      },
      {
        id: 'metadata',
        titleKey: 'help.archive.metadata.title',
        introKey: 'help.archive.metadata.intro',
        steps: [
          { titleKey: 'help.archive.metadata.step1.title', descriptionKey: 'help.archive.metadata.step1.desc' },
          { titleKey: 'help.archive.metadata.step2.title', descriptionKey: 'help.archive.metadata.step2.desc' },
          { titleKey: 'help.archive.metadata.step3.title', descriptionKey: 'help.archive.metadata.step3.desc' },
        ],
        tips: [
          { textKey: 'help.archive.metadata.tip1', variant: 'tip' },
        ],
      },
      {
        id: 'related-documents',
        titleKey: 'help.archive.related.title',
        introKey: 'help.archive.related.intro',
        steps: [
          { titleKey: 'help.archive.related.step1.title', descriptionKey: 'help.archive.related.step1.desc' },
          { titleKey: 'help.archive.related.step2.title', descriptionKey: 'help.archive.related.step2.desc' },
        ],
      },
    ],
  },

  // ----------------------------------------------------------
  // 6. Track Status
  // ----------------------------------------------------------
  {
    id: 'status',
    titleKey: 'help.status.title',
    descriptionKey: 'help.status.description',
    icon: BarChart3,
    sections: [
      {
        id: 'status-overview',
        titleKey: 'help.status.overview.title',
        introKey: 'help.status.overview.intro',
        steps: [
          { titleKey: 'help.status.overview.step1.title', descriptionKey: 'help.status.overview.step1.desc' },
          { titleKey: 'help.status.overview.step2.title', descriptionKey: 'help.status.overview.step2.desc' },
          { titleKey: 'help.status.overview.step3.title', descriptionKey: 'help.status.overview.step3.desc' },
        ],
      },
      {
        id: 'document-details',
        titleKey: 'help.status.details.title',
        introKey: 'help.status.details.intro',
        steps: [
          { titleKey: 'help.status.details.step1.title', descriptionKey: 'help.status.details.step1.desc' },
          { titleKey: 'help.status.details.step2.title', descriptionKey: 'help.status.details.step2.desc' },
          { titleKey: 'help.status.details.step3.title', descriptionKey: 'help.status.details.step3.desc' },
          { titleKey: 'help.status.details.step4.title', descriptionKey: 'help.status.details.step4.desc' },
        ],
      },
      {
        id: 'download-signed',
        titleKey: 'help.status.download.title',
        introKey: 'help.status.download.intro',
        steps: [
          { titleKey: 'help.status.download.step1.title', descriptionKey: 'help.status.download.step1.desc' },
          { titleKey: 'help.status.download.step2.title', descriptionKey: 'help.status.download.step2.desc' },
        ],
      },
    ],
  },

  // ----------------------------------------------------------
  // 7. Troubleshooting
  // ----------------------------------------------------------
  {
    id: 'troubleshooting',
    titleKey: 'help.troubleshooting.title',
    descriptionKey: 'help.troubleshooting.description',
    icon: HelpCircle,
    sections: [
      {
        id: 'doc-not-showing',
        titleKey: 'help.troubleshooting.noDoc.title',
        introKey: 'help.troubleshooting.noDoc.intro',
        steps: [
          { titleKey: 'help.troubleshooting.noDoc.step1.title', descriptionKey: 'help.troubleshooting.noDoc.step1.desc' },
          { titleKey: 'help.troubleshooting.noDoc.step2.title', descriptionKey: 'help.troubleshooting.noDoc.step2.desc' },
          { titleKey: 'help.troubleshooting.noDoc.step3.title', descriptionKey: 'help.troubleshooting.noDoc.step3.desc' },
        ],
      },
      {
        id: 'cannot-send',
        titleKey: 'help.troubleshooting.cantSend.title',
        introKey: 'help.troubleshooting.cantSend.intro',
        steps: [
          { titleKey: 'help.troubleshooting.cantSend.step1.title', descriptionKey: 'help.troubleshooting.cantSend.step1.desc' },
          { titleKey: 'help.troubleshooting.cantSend.step2.title', descriptionKey: 'help.troubleshooting.cantSend.step2.desc' },
          { titleKey: 'help.troubleshooting.cantSend.step3.title', descriptionKey: 'help.troubleshooting.cantSend.step3.desc' },
        ],
      },
      {
        id: 'addin-not-loading',
        titleKey: 'help.troubleshooting.notLoading.title',
        introKey: 'help.troubleshooting.notLoading.intro',
        steps: [
          { titleKey: 'help.troubleshooting.notLoading.step1.title', descriptionKey: 'help.troubleshooting.notLoading.step1.desc' },
          { titleKey: 'help.troubleshooting.notLoading.step2.title', descriptionKey: 'help.troubleshooting.notLoading.step2.desc' },
          { titleKey: 'help.troubleshooting.notLoading.step3.title', descriptionKey: 'help.troubleshooting.notLoading.step3.desc' },
        ],
      },
      {
        id: 'session-expired',
        titleKey: 'help.troubleshooting.session.title',
        introKey: 'help.troubleshooting.session.intro',
        steps: [
          { titleKey: 'help.troubleshooting.session.step1.title', descriptionKey: 'help.troubleshooting.session.step1.desc' },
          { titleKey: 'help.troubleshooting.session.step2.title', descriptionKey: 'help.troubleshooting.session.step2.desc' },
        ],
        tips: [
          { textKey: 'help.troubleshooting.session.tip1', variant: 'tip' },
        ],
      },
    ],
  },
]
